import React, { useEffect, useState, useCallback } from "react";
import { Truck, Plus, Save, Trash2, Star, Power } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";

const BLANK = {
    code: "",
    name: "",
    description: "",
    price: 0,
    active: true,
    is_default: false,
    sort_order: 0,
    free_above: "",
};

export const AdminShippingPanel = ({ token }) => {
    const headers = { "X-Admin-Token": token };
    const [methods, setMethods] = useState([]);
    const [loading, setLoading] = useState(false);
    const [showCreate, setShowCreate] = useState(false);
    const [draft, setDraft] = useState(BLANK);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/admin/shipping-methods", { headers });
            setMethods(Array.isArray(data) ? data : []);
        } catch {
            toast.error("Could not load shipping methods");
        }
        setLoading(false);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [token]);

    useEffect(() => { load(); }, [load]);

    const create = async () => {
        if (!draft.code.trim() || !draft.name.trim()) {
            toast.error("Code and name are required");
            return;
        }
        const payload = {
            ...draft,
            code: draft.code.trim().toLowerCase().replace(/[^a-z0-9_-]/g, "-"),
            price: Number(draft.price || 0),
            sort_order: Number(draft.sort_order || 0),
            free_above: draft.free_above === "" || draft.free_above === null ? null : Number(draft.free_above),
        };
        try {
            await api.post("/admin/shipping-methods", payload, { headers });
            toast.success("Shipping method created");
            setShowCreate(false);
            setDraft(BLANK);
            load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Create failed");
        }
    };

    const patch = async (code, patchBody) => {
        try {
            await api.patch(`/admin/shipping-methods/${code}`, patchBody, { headers });
            load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Update failed");
        }
    };

    const remove = async (code) => {
        if (!window.confirm(`Delete shipping method "${code}"? Customers won't be able to select it after this.`)) return;
        try {
            await api.delete(`/admin/shipping-methods/${code}`, { headers });
            toast.success("Deleted");
            load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Delete failed");
        }
    };

    return (
        <div className="space-y-5" data-testid="admin-shipping-panel">
            <div className="flex flex-wrap items-center justify-between gap-3">
                <div>
                    <h2 className="font-display text-xl font-bold text-white flex items-center gap-2">
                        <Truck size={18} className="text-[var(--gold)]" /> Shipping methods
                    </h2>
                    <p className="text-xs text-[var(--text-muted)] font-mono mt-1">
                        Customers pick one of these at checkout. The default is auto-selected.
                    </p>
                </div>
                <button
                    onClick={() => setShowCreate((s) => !s)}
                    className="btn-neon"
                    data-testid="shipping-new-button"
                >
                    <Plus size={14} /> New method
                </button>
            </div>

            {showCreate && (
                <div className="glass-elevated rounded-2xl p-5 border border-[var(--gold)]/30 space-y-3" data-testid="shipping-create-form">
                    <p className="text-[10px] font-mono uppercase tracking-[0.25em] text-[var(--text-muted)]">New shipping method</p>
                    <div className="grid md:grid-cols-2 gap-3">
                        <LabeledInput label="Code (slug)" value={draft.code} onChange={(v) => setDraft((d) => ({ ...d, code: v }))} placeholder="e.g. overnight" testid="shipping-code-input" />
                        <LabeledInput label="Display name" value={draft.name} onChange={(v) => setDraft((d) => ({ ...d, name: v }))} placeholder="Overnight Courier" testid="shipping-name-input" />
                        <LabeledInput label="Description (optional)" value={draft.description} onChange={(v) => setDraft((d) => ({ ...d, description: v }))} placeholder="Next business day" testid="shipping-desc-input" />
                        <LabeledInput label="Price (AUD)" type="number" value={draft.price} onChange={(v) => setDraft((d) => ({ ...d, price: v }))} testid="shipping-price-input" />
                        <LabeledInput label="Sort order" type="number" value={draft.sort_order} onChange={(v) => setDraft((d) => ({ ...d, sort_order: v }))} testid="shipping-sort-input" />
                        <LabeledInput label="Free above (AUD, optional)" type="number" value={draft.free_above} onChange={(v) => setDraft((d) => ({ ...d, free_above: v }))} placeholder="Leave empty for never" testid="shipping-freeabove-input" />
                    </div>
                    <div className="flex items-center gap-4">
                        <ToggleField label="Active" value={draft.active} onChange={(v) => setDraft((d) => ({ ...d, active: v }))} testid="shipping-active-toggle" />
                        <ToggleField label="Default" value={draft.is_default} onChange={(v) => setDraft((d) => ({ ...d, is_default: v }))} testid="shipping-default-toggle" />
                    </div>
                    <div className="flex gap-2 justify-end">
                        <button
                            onClick={() => { setShowCreate(false); setDraft(BLANK); }}
                            className="px-4 py-2 rounded-full border border-white/10 text-xs font-mono uppercase tracking-widest text-[var(--text-secondary)] hover:border-white/20"
                            data-testid="shipping-cancel"
                        >
                            Cancel
                        </button>
                        <button onClick={create} className="btn-solid" data-testid="shipping-save">
                            <Save size={14} /> Create
                        </button>
                    </div>
                </div>
            )}

            <div className="space-y-3">
                {loading && methods.length === 0 && (
                    <div className="text-sm text-[var(--text-muted)] font-mono">Loading…</div>
                )}
                {!loading && methods.length === 0 && (
                    <div className="glass rounded-2xl p-8 text-center text-[var(--text-secondary)]" data-testid="shipping-empty">
                        No shipping methods configured.
                    </div>
                )}
                {methods.map((m) => (
                    <ShippingRow
                        key={m.code}
                        method={m}
                        onPatch={(p) => patch(m.code, p)}
                        onDelete={() => remove(m.code)}
                    />
                ))}
            </div>
        </div>
    );
};

const ShippingRow = ({ method, onPatch, onDelete }) => {
    const [edit, setEdit] = useState({
        name: method.name,
        description: method.description || "",
        price: method.price,
        sort_order: method.sort_order || 0,
        free_above: method.free_above ?? "",
    });
    const [dirty, setDirty] = useState(false);

    const set = (k, v) => { setEdit((e) => ({ ...e, [k]: v })); setDirty(true); };

    const save = () => {
        const patch = {
            name: edit.name,
            description: edit.description || null,
            price: Number(edit.price || 0),
            sort_order: Number(edit.sort_order || 0),
            free_above: edit.free_above === "" || edit.free_above === null ? null : Number(edit.free_above),
        };
        onPatch(patch);
        setDirty(false);
    };

    return (
        <div className="glass-elevated rounded-2xl p-5" data-testid={`shipping-row-${method.code}`}>
            <div className="flex flex-wrap items-start justify-between gap-3 mb-3">
                <div>
                    <div className="flex items-center gap-2">
                        <span className="font-display text-lg font-bold text-white">{method.name}</span>
                        <span className="text-[10px] font-mono uppercase tracking-widest px-2 py-0.5 rounded-full border border-white/10 text-[var(--text-muted)]">
                            {method.code}
                        </span>
                        {method.is_default && (
                            <span className="text-[10px] font-mono uppercase tracking-widest px-2 py-0.5 rounded-full border border-[var(--gold)]/40 text-[var(--gold)] bg-[rgba(233,188,11,0.08)] inline-flex items-center gap-1">
                                <Star size={10} /> Default
                            </span>
                        )}
                        {!method.active && (
                            <span className="text-[10px] font-mono uppercase tracking-widest px-2 py-0.5 rounded-full border border-red-500/40 text-red-300 bg-red-500/10">
                                Disabled
                            </span>
                        )}
                    </div>
                </div>
                <div className="flex items-center gap-2">
                    <button
                        onClick={() => onPatch({ is_default: true })}
                        disabled={method.is_default}
                        className="px-3 py-1.5 rounded-full border border-[var(--gold)]/30 text-[var(--gold)] hover:bg-[var(--gold)]/10 text-[10px] font-mono uppercase tracking-widest disabled:opacity-30 inline-flex items-center gap-1"
                        data-testid={`shipping-set-default-${method.code}`}
                    >
                        <Star size={11} /> Default
                    </button>
                    <button
                        onClick={() => onPatch({ active: !method.active })}
                        className={`px-3 py-1.5 rounded-full border text-[10px] font-mono uppercase tracking-widest inline-flex items-center gap-1 ${
                            method.active
                                ? "border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/10"
                                : "border-red-500/40 text-red-300 hover:bg-red-500/10"
                        }`}
                        data-testid={`shipping-toggle-${method.code}`}
                    >
                        <Power size={11} /> {method.active ? "Active" : "Inactive"}
                    </button>
                    <button
                        onClick={onDelete}
                        className="px-3 py-1.5 rounded-full border border-red-500/30 text-red-300 hover:bg-red-500/10 text-[10px] font-mono uppercase tracking-widest inline-flex items-center gap-1"
                        data-testid={`shipping-delete-${method.code}`}
                    >
                        <Trash2 size={11} /> Delete
                    </button>
                </div>
            </div>
            <div className="grid md:grid-cols-2 gap-3">
                <LabeledInput label="Display name" value={edit.name} onChange={(v) => set("name", v)} testid={`shipping-name-${method.code}`} />
                <LabeledInput label="Description" value={edit.description} onChange={(v) => set("description", v)} testid={`shipping-desc-${method.code}`} />
                <LabeledInput label="Price (AUD)" type="number" value={edit.price} onChange={(v) => set("price", v)} testid={`shipping-price-${method.code}`} />
                <LabeledInput label="Sort order" type="number" value={edit.sort_order} onChange={(v) => set("sort_order", v)} testid={`shipping-sort-${method.code}`} />
                <LabeledInput label="Free above (AUD)" type="number" value={edit.free_above ?? ""} onChange={(v) => set("free_above", v)} placeholder="Leave blank for never" testid={`shipping-freeabove-${method.code}`} />
            </div>
            {dirty && (
                <div className="mt-3 flex justify-end">
                    <button onClick={save} className="btn-solid" data-testid={`shipping-save-${method.code}`}>
                        <Save size={14} /> Save changes
                    </button>
                </div>
            )}
        </div>
    );
};

const LabeledInput = ({ label, value, onChange, type = "text", placeholder, testid }) => (
    <label className="block">
        <span className="text-[10px] font-mono uppercase tracking-[0.25em] text-[var(--text-muted)] block mb-1">{label}</span>
        <input
            type={type}
            value={value ?? ""}
            onChange={(e) => onChange(e.target.value)}
            placeholder={placeholder}
            className="w-full px-3 py-2 bg-black/40 border border-white/10 rounded-lg text-sm text-white placeholder:text-[var(--text-muted)] focus:outline-none focus:border-[var(--gold)]"
            data-testid={testid}
        />
    </label>
);

const ToggleField = ({ label, value, onChange, testid }) => (
    <label className="inline-flex items-center gap-2 cursor-pointer">
        <input
            type="checkbox"
            checked={!!value}
            onChange={(e) => onChange(e.target.checked)}
            className="w-4 h-4 accent-[var(--gold)]"
            data-testid={testid}
        />
        <span className="text-xs font-mono uppercase tracking-widest text-[var(--text-secondary)]">{label}</span>
    </label>
);
